/**
 * Title-case each word of a free-text field value: "john carter" → "John
 * Carter", "55 harbour square" → "55 Harbour Square". Only the first letter of
 * each word is uppercased; the rest is left as typed (so acronyms the user
 * enters in caps are preserved). Applied as the user types on screening text
 * fields (names, addresses, company/occupation) — never on email, amount,
 * phone, or date fields (see `SKIP_CAPITALIZE`).
 */
export function capitalizeWords(value: string): string {
	return value.replace(/(^|\s|[-/])(\p{L})/gu, (_m, sep: string, ch: string) =>
		sep === undefined ? ch : sep + ch.toUpperCase(),
	)
}

/**
 * Field-name matcher for values that must NOT be auto-capitalized: emails,
 * money amounts, phone numbers, dates, and structured codes.
 */
// `rent` is anchored to the end (rent$) so it matches the money fields
// currentRent / previousRent WITHOUT also matching the "rent" inside
// "cur​rent…" (which wrongly skipped currentLandlordName, currentAddress, etc).
const SKIP_CAPITALIZE =
	/email|rent$|income|amount|salary|phone|date|dob|postal|zip|country|code|number|url|website|status/i

/** Auto-capitalize a text field's value unless its name marks it as non-text. */
export function capitalizeFieldValue(field: string, value: string): string {
	if (SKIP_CAPITALIZE.test(field)) return value
	// Yes/No radio values are enums, never free text — title-casing them to
	// "Yes"/"No" breaks the radio's `=== 'yes'` checked comparison and the
	// `=== 'yes'` validation/submit checks (e.g. maternityLeave, governmentAssistance).
	if (value === 'yes' || value === 'no') return value
	return capitalizeWords(value)
}

/**
 * Strip digits and special characters from a name-style value, keeping only
 * letters (any script), spaces, hyphens and apostrophes — so "O'Brien",
 * "Jean-Paul" and "Mary Jane" survive while "John3!" → "John". Applied as the
 * user types on first/last name, company name, and occupation fields.
 */
export function sanitizeName(value: string): string {
	return value.replace(/[^\p{L}\s'-]/gu, '')
}

/**
 * Landlord names are letters and spaces only — no digits, hyphens,
 * apostrophes, or other punctuation.
 */
export function sanitizeAlphabeticName(value: string): string {
	return value.replace(/[^\p{L}\s]/gu, '')
}

/**
 * Field-name matcher for the name/occupation text fields that must reject
 * digits and special characters. Matches firstName, lastName, companyName,
 * landlordName, occupation, … but NOT phone/number/address/amount fields.
 */
const NAME_TEXT_FIELD = /name$|occupation/i
const LANDLORD_NAME_FIELD = /landlordName$/i

/**
 * Normalize a text field's value as the user types: strip digits/specials from
 * name-style fields (see `sanitizeName`), then apply the title-case rule. Keyed
 * by the field name; non-name fields fall through to plain capitalization.
 */
export function normalizeTextFieldValue(field: string, value: string): string {
	const cleaned = LANDLORD_NAME_FIELD.test(field)
		? sanitizeAlphabeticName(value)
		: NAME_TEXT_FIELD.test(field)
			? sanitizeName(value)
			: value
	return capitalizeFieldValue(field, cleaned)
}
